import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Images } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PricingGate } from "@/components/dealer/PricingGate";
import { BrandLockup } from "@/components/BrandLockup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CustomerViewProvider, useCustomerView } from "@/context/CustomerViewContext";
import { CopilotProvider } from "@/context/CopilotContext";
import CopilotLauncher from "@/components/copilot/CopilotLauncher";
import { useEffect, useState } from "react";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useDealerActionCounts } from "@/hooks/useDealerActionCounts";
import { useUnseenReports } from "@/hooks/useUnseenReports";
import { api } from "@/lib/api";
import { US_STATES } from "@/lib/usStates";
import {
  Compass,
  MessagesSquare,
  FolderKanban,
  CalendarClock,
  FlaskConical,
  FileCheck,
  Sparkles,
  ShoppingCart,
  Bell,
  LogOut,
  Menu,
  UsersRound,
  UserRound,
  FileText,
  Flag,
  BookOpen,
  BadgeDollarSign,
  Warehouse,
  Ship,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dealer", label: "Home", icon: Compass, end: true },
  { to: "/dealer/profile", label: "My Profile", icon: UserRound },
  { to: "/dealer/notifications", label: "Notifications", icon: Bell },
  { to: "/dealer/copilot", label: "ZELI", icon: MessagesSquare },
  { to: "/dealer/discover", label: "Catalog", icon: Compass },
  { to: "/dealer/media", label: "Media Library", icon: Images },
  { to: "/dealer/projects", label: "Projects", icon: FolderKanban },
  { to: "/dealer/proposals", label: "Quotes", icon: FileText },
  { to: "/dealer/pricing", label: "Pricing", icon: BadgeDollarSign },
  { to: "/dealer/inventory", label: "Inventory", icon: Warehouse },
  { to: "/dealer/shipments", label: "Shipments", icon: Ship },
  { to: "/dealer/sourcing", label: "Sourcing", icon: Ship },
  { to: "/dealer/custom-studio", label: "Custom Studio", icon: Sparkles },
  { to: "/dealer/samples", label: "Samples", icon: FlaskConical },
  { to: "/dealer/reservations", label: "Reservations", icon: CalendarClock },
  { to: "/dealer/orders", label: "Orders", icon: ShoppingCart },
  { to: "/dealer/certificate", label: "Certificates", icon: FileCheck },
  { to: "/dealer/reports", label: "Reports", icon: Flag },
  { to: "/dealer/library", label: "Technical Library", icon: BookOpen },
  { to: "/dealer/team", label: "Team", icon: UsersRound, adminOnly: true },
];

// V4.8 RC48-ATT-001…004: actionable module badges on the dealer nav
const DEALER_ACTION_BADGES = { Samples: "samples_action", Quotes: "quotes_action", Reservations: "reservations_action", Orders: "orders_action" };

function NavLinks({ onNavigate, mobile = false, unread = 0, isAdmin = false, reportsUnseen = 0, counts = {} }) {
  return (
    <nav className="mt-8 space-y-1 px-4" data-testid={mobile ? "dealer-nav-mobile" : "dealer-nav"}>
      {NAV.filter((n) => !n.adminOnly || isAdmin).map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`dealer-nav-${mobile ? "mobile-" : ""}${label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
              isActive
                ? "bg-white font-medium text-charcoal shadow-sm"
                : "text-warmgrey hover:bg-[#EAE8E3] hover:text-charcoal"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
          {DEALER_ACTION_BADGES[label] && counts[DEALER_ACTION_BADGES[label]] > 0 && (
            <span
              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta px-1.5 text-[10px] font-bold text-white"
              data-testid={`dealer-action-badge${mobile ? "-mobile" : ""}-${label.toLowerCase()}`}
            >
              {counts[DEALER_ACTION_BADGES[label]]}
            </span>
          )}
          {label === "Reports" && reportsUnseen > 0 && (
            <span
              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta px-1.5 text-[10px] font-bold text-white"
              data-testid={mobile ? "reports-badge-mobile" : "reports-badge"}
            >
              {reportsUnseen}
            </span>
          )}
          {label === "Notifications" && unread > 0 && (
            <span
              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta px-1.5 text-[10px] font-bold text-white"
              data-testid={mobile ? "notif-badge-mobile" : "notif-badge"}
            >
              {unread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function CustomerViewToggle() {
  const { customerView, toggleCustomerView } = useCustomerView();
  const [tradeWarnOpen, setTradeWarnOpen] = useState(false);
  return (
    <div className="flex items-center gap-2" data-testid="customer-view-toggle-container">
      {/* §13 MODE-002/003/004/048: compact two-state TRADE | CUSTOMER control — the control
          itself makes the active disclosure state unmistakable; MODE-005/MIG-MODE-003: no
          separate persistent banner. */}
      <div className="flex overflow-hidden rounded-md border border-border bg-white" role="group" aria-label="Display mode">
        <button type="button" aria-pressed={!customerView}
          onClick={() => { if (customerView) setTradeWarnOpen(true); }} data-testid="mode-trade"
          className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${!customerView ? "bg-charcoal text-white" : "text-warmgrey hover:text-charcoal"}`}>
          Trade
        </button>
        <button type="button" aria-pressed={customerView}
          onClick={() => { if (!customerView) toggleCustomerView(); }} data-testid="mode-customer"
          className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${customerView ? "bg-terracotta text-white" : "text-warmgrey hover:text-charcoal"}`}>
          Customer
        </button>
      </div>
      {/* §13 MODE-012/051/052: every deliberate Customer → Trade transition presents this
          acknowledgement before restoring dealer-private presentation. MODE-013: nothing
          persistent remains after it. MODE-053: no memory duration invented — the warning
          appears on each deliberate transition. MODE-014: Customer needs no warning. */}
      <AlertDialog open={tradeWarnOpen} onOpenChange={setTradeWarnOpen}>
        <AlertDialogContent data-testid="trade-warning-dialog" className="border-border bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-medium text-charcoal">Switch to Trade Mode?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-warmgrey">
              Trade Mode may display Zelligio trade pricing, margins, and other dealer-private
              information that is not safe to show customers. Make sure no customer can see
              your screen before continuing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trade-warning-cancel" className="border-limestone text-charcoal hover:bg-sand">
              Stay in Customer Mode
            </AlertDialogCancel>
            <AlertDialogAction data-testid="trade-warning-confirm" onClick={toggleCustomerView}
              className="bg-terracotta text-white hover:bg-terracotta-hover">
              I understand — switch to Trade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DealerShellInner() {
  const { user, logout } = useAuth();
  const myRole = user?.dealer_role || (user?.location_scope === "location" ? "LOCATION_MANAGER" : "DEALER_ADMIN");
  const isDealerAdmin = myRole === "DEALER_ADMIN";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unread = useUnreadNotifications();
  const actionCounts = useDealerActionCounts();
  const reportsUnseen = useUnseenReports();
  const [cert, setCert] = useState(null);
  const location = useLocation();
  const isZeli = location.pathname.startsWith("/dealer/copilot");  // RC51-QA-001: ZELI workspace fills the viewport
  useEffect(() => {
    // §27.3: operational sans-serif typography across the authenticated workspace
    document.body.classList.add("connect-ops");
    return () => document.body.classList.remove("connect-ops");
  }, []);
  useEffect(() => {
    const loadCert = () => api.get("/dealer/certificate/status").then((r) => setCert(r.data)).catch(() => {});
    loadCert();
    window.addEventListener("certificate-status-changed", loadCert);
    window.addEventListener("focus", loadCert);
    return () => {
      window.removeEventListener("certificate-status-changed", loadCert);
      window.removeEventListener("focus", loadCert);
    };
    // refetch on every navigation + window focus: the banner must always derive from
    // the CURRENT authoritative status — admin-side approval/rejection happens in
    // another session and must never leave stale pending-review messaging behind
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen overflow-hidden bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-[280px] flex-col bg-sidebar md:flex">
        <div className="px-7 pt-8">
          <p className="font-display text-2xl font-medium tracking-tight text-charcoal">
            <BrandLockup className="font-display text-2xl font-medium tracking-tight text-charcoal" />
          </p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {user?.dealer_name || "Dealer Portal"}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavLinks unread={unread} isAdmin={isDealerAdmin} reportsUnseen={reportsUnseen} counts={actionCounts} />
        </div>
        <div className="border-t border-border p-4">
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-charcoal">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            data-testid="dealer-logout-button"
            onClick={handleLogout}
            className="mt-1 w-full justify-start gap-3 text-warmgrey hover:text-charcoal"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className={`flex min-h-screen min-w-0 flex-1 flex-col md:ml-[280px] ${isZeli ? "md:h-screen md:overflow-hidden" : ""}`}>
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between gap-4 border-b border-border bg-background/80 px-6 backdrop-blur-md md:px-12">
          <div className="flex items-center gap-4">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  data-testid="dealer-mobile-menu-button"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="flex w-[280px] flex-col bg-sidebar p-0">
                <SheetTitle className="sr-only">Dealer menu</SheetTitle>
                <div className="px-7 pt-8">
                  <p className="font-display text-2xl font-medium tracking-tight text-charcoal">
                    <BrandLockup className="font-display text-2xl font-medium tracking-tight text-charcoal" />
                  </p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    {user?.dealer_name || "Dealer Portal"}
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <NavLinks mobile unread={unread} onNavigate={() => setOpen(false)} isAdmin={isDealerAdmin} reportsUnseen={reportsUnseen} counts={actionCounts} />
                </div>
                <div className="border-t border-border p-4">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-charcoal">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    data-testid="dealer-logout-button-mobile"
                    onClick={handleLogout}
                    className="mt-1 w-full justify-start gap-3 text-warmgrey hover:text-charcoal"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <BrandLockup className="font-display text-lg font-medium text-charcoal md:hidden" />
          </div>
          <CustomerViewToggle />
        </header>
        <main className={isZeli ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4 md:p-6" : "min-w-0 flex-1 p-6 pb-[calc(2rem+env(safe-area-inset-bottom))] md:p-12 lg:p-16"} data-testid="dealer-main">
          <div className={isZeli ? "page-enter flex min-h-0 flex-1 flex-col" : "page-enter mx-auto max-w-6xl"}>
            {cert && (cert.status === "EXPIRED" || cert.renewal_warning || cert.status === "REJECTED" || (cert.uncovered_states || []).length > 0 || !cert.uploaded) && (
              <div
                className="mb-8 flex flex-wrap items-center gap-3 rounded-md border border-status-reserved/40 bg-[#D4A373]/10 p-4 text-sm text-[#8a6435]"
                data-testid="certificate-banner"
              >
                <FileCheck className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  {cert.status === "EXPIRED"
                    ? "Your resale certificate has expired. You may continue purchasing with applicable sales tax until a current replacement is approved."
                    : cert.renewal_warning
                      ? `Your resale certificate expires on ${cert.expiration_date} (${cert.days_remaining} day${cert.days_remaining === 1 ? "" : "s"} left) — upload your renewal now; the current certificate stays active until it expires.`
                      : cert.status === "REJECTED"
                        ? `Your resale certificate was not approved${cert.review_note ? ` — ${cert.review_note}` : ""}. Please upload a corrected certificate for review.`
                        : (cert.pending_states || []).length > 0
                          ? `Your resale certificate for ${cert.pending_states.map((s) => US_STATES[s] || s).join(", ")} is awaiting review. You may continue purchasing; applicable sales tax will be charged until your resale exemption is approved.`
                          : (cert.uncovered_states || []).length > 0
                            ? `No current resale certificate on file for ${cert.uncovered_states.map((s) => US_STATES[s] || s).join(", ")} — purchases shipped there will be charged applicable sales tax. Upload a certificate for review if you qualify for resale exemption.`
                            : cert.uploaded
                              ? "Your resale certificate is awaiting review. You may continue purchasing; applicable sales tax will be charged until your resale exemption is approved."
                              : "You may purchase without a resale certificate — applicable sales tax will be charged. Upload a resale certificate for review if you qualify for resale exemption."}
                </span>
                <Link to="/dealer/certificate" data-testid="certificate-banner-link" className="whitespace-nowrap font-medium underline">
                  {cert.status === "EXPIRED" || cert.renewal_warning ? "Upload renewal" : (cert.pending_states || []).length > 0 || cert.uploaded ? "View status" : "Upload certificate"}
                </Link>
              </div>
            )}
            <PricingGate><Outlet /></PricingGate>
          </div>
        </main>
        <CopilotLauncher />
      </div>
    </div>
  );
}

export default function DealerShell() {
  return (
    <CustomerViewProvider>
      <CopilotProvider>
        <DealerShellInner />
      </CopilotProvider>
    </CustomerViewProvider>
  );
}
